/**
 * UpgradeMedicProvider — dashboard summary stats with filter support.
 *
 * Fetches Lambda inventory, account details (team/manager/owner), and job
 * history on mount. Stores raw data so FinTechOpsSelectionFilters can
 * recompute filtered dashboard stats when dashboard filters change.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsMessages } from '../../FinTechOpsConstants';
import { UpgradeMedicDataService } from 'src/services/upgrade-medic/UpgradeMedicDataService';
import { LAMBDA_CONFIG } from '../../upgrade-medic/config';
import { Account, AccountDetailsMap } from '../../upgrade-medic/types';

export interface UpgradeMedicDashboardData {
  totalLambdas: number;
  eolLambdas: number;
  totalPipelines: number;
  eolPercentage: number;
  totalJobExecutions: number;
}

const EMPTY_DASHBOARD: UpgradeMedicDashboardData = {
  totalLambdas: 0,
  eolLambdas: 0,
  totalPipelines: 0,
  eolPercentage: 0,
  totalJobExecutions: 0
};

export interface UpgradeMedicContextValue {
  upgradeMedicStatus: FiltersStatus;
  setUpgradeMedicStatus: (s: FiltersStatus) => void;
  upgradeMedicDashboardData: UpgradeMedicDashboardData;
  setUpgradeMedicDashboardData: (d: UpgradeMedicDashboardData) => void;
  /** Raw accounts for filter recomputation. */
  upgradeMedicAccounts: Account[];
  /** Account details map (accountName → { manager, owner, team }). */
  upgradeMedicAccountDetailsMap: AccountDetailsMap;
  /** Total job executions (unaffected by filters). */
  upgradeMedicTotalJobExecutions: number;
}

const defaultValue: UpgradeMedicContextValue = {
  upgradeMedicStatus: 'loading',
  setUpgradeMedicStatus: () => {},
  upgradeMedicDashboardData: EMPTY_DASHBOARD,
  setUpgradeMedicDashboardData: () => {},
  upgradeMedicAccounts: [],
  upgradeMedicAccountDetailsMap: new Map(),
  upgradeMedicTotalJobExecutions: 0
};

const UpgradeMedicContext = createContext<UpgradeMedicContextValue>(defaultValue);

export const useUpgradeMedic = () => useContext(UpgradeMedicContext);

/** Compute dashboard stats from a (possibly filtered) account list. */
export const computeUpgradeMedicDashboardData = (accounts: Account[], totalJobExecutions: number): UpgradeMedicDashboardData => {
  const eolVersionsSet = new Set(LAMBDA_CONFIG.eolVersions);
  const pipelineNames = new Set<string>();
  let totalLambdas = 0;
  let eolLambdas = 0;

  accounts.forEach((account) => {
    account.pipelines.forEach((pipeline) => {
      pipelineNames.add(pipeline.name);
      pipeline.lambdas.forEach((lambda) => {
        totalLambdas++;
        if (eolVersionsSet.has(lambda.runtime)) eolLambdas++;
      });
    });
  });

  const eolPercentage = totalLambdas > 0 ? Number(((eolLambdas / totalLambdas) * 100).toFixed(1)) : 0;
  return { totalLambdas, eolLambdas, totalPipelines: pipelineNames.size, eolPercentage, totalJobExecutions };
};

interface Props {
  children: React.ReactNode;
}

export const UpgradeMedicProvider = ({ children }: Props) => {
  const { displayNotification } = useNotification();
  const [dashboard, setDashboard] = useState<UpgradeMedicDashboardData>(EMPTY_DASHBOARD);
  const [status, setStatus] = useState<FiltersStatus>('loading');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountDetailsMap, setAccountDetailsMap] = useState<AccountDetailsMap>(new Map());
  const [totalJobExecutions, setTotalJobExecutions] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchDashboardStats = async () => {
      setStatus('loading');
      try {
        const [fetchedAccounts, jobHistory, detailsMap] = await Promise.all([
          UpgradeMedicDataService.getLambdaInventory(),
          UpgradeMedicDataService.getJobRunHistory('Lambda', 500),
          UpgradeMedicDataService.getAccountDetails()
        ]);

        if (cancelled) return;

        setAccounts(fetchedAccounts);
        setAccountDetailsMap(detailsMap);
        setTotalJobExecutions(jobHistory.length);
        setDashboard(computeUpgradeMedicDashboardData(fetchedAccounts, jobHistory.length));
        setStatus('finished');
      } catch (err) {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Unable to load Upgrade Medic dashboard data', error);
        setStatus('error');
        displayNotification('error', FinTechOpsMessages.APIError);
      }
    };

    fetchDashboardStats();
    return () => {
      cancelled = true;
    };
  }, [displayNotification]);

  const setUpgradeMedicStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setUpgradeMedicDashboardData = useCallback((d: UpgradeMedicDashboardData) => setDashboard(d), []);

  const value = useMemo<UpgradeMedicContextValue>(
    () => ({
      upgradeMedicStatus: status,
      setUpgradeMedicStatus,
      upgradeMedicDashboardData: dashboard,
      setUpgradeMedicDashboardData,
      upgradeMedicAccounts: accounts,
      upgradeMedicAccountDetailsMap: accountDetailsMap,
      upgradeMedicTotalJobExecutions: totalJobExecutions
    }),
    [status, setUpgradeMedicStatus, dashboard, setUpgradeMedicDashboardData, accounts, accountDetailsMap, totalJobExecutions]
  );

  return <UpgradeMedicContext.Provider value={value}>{children}</UpgradeMedicContext.Provider>;
};
